import { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import SamErpik from "@/pages/SamErpik";
// Non-home routes are lazy-loaded so the home bundle stays small (improves
// LCP for first-time visitors landing on `/`, which is the vast majority).
const CaseStudy = lazy(() => import("@/pages/CaseStudy"));
const Lab       = lazy(() => import("@/pages/Lab"));
const Writing   = lazy(() => import("@/pages/Writing"));
const Post      = lazy(() => import("@/pages/Post"));
const Garage    = lazy(() => import("@/pages/Garage"));
const NotFound  = lazy(() => import("@/pages/NotFound"));
import AppLoader from "@/components/AppLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import CustomCursor from "@/components/CustomCursor";
import CommandPalette from "@/components/CommandPalette";
import ContactOverlay from "@/components/ContactOverlay";
import Terminal from "@/components/Terminal";
import { useLenis } from "@/lib/useLenis";
import { initInputListeners } from "@/lib/inputStore";
import { DarkModeProvider, useDarkMode } from "@/lib/darkMode";
import { printConsoleGuard } from "@/lib/consoleGuard";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;

    // If the new URL has a hash (e.g. /#signal from a cross-route nav link),
    // wait a frame for the destination route to mount, then scroll to that
    // element. Otherwise default to scrolling the page to the top.
    if (hash) {
      const id = hash.replace(/^#/, "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) {
          const top = el.getBoundingClientRect().top + window.scrollY - 48;
          window.__lenis.scrollTo(top, { duration: 1.2 });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
      return;
    }

    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      document.documentElement.scrollTop = 0;
    }
  }, [pathname, hash]);
  return null;
}

// Cheeky toast that fires on every theme toggle
function ThemeToast() {
  const { dark, count } = useDarkMode();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (count === 0) return; // ignore initial mount
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 2400);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={count}
          initial={{ opacity: 0, y: 14, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] px-5 py-2.5 rounded-full font-mono text-[11px] tracking-[0.2em] uppercase font-semibold shadow-2xl pointer-events-none select-none"
          style={{
            background: dark ? "#E6EDF3" : "#1A1A1A",
            color:      dark ? "#0D1117" : "#E6EDF3",
          }}
        >
          {dark ? "Welcome to the dark side." : "Alright, lights on."}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AppShell() {
  useLenis();
  const { pathname } = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState("");

  useEffect(() => {
    const off = initInputListeners();
    return off;
  }, []);

  // Self-XSS console warning — one time, on app load.
  useEffect(() => { printConsoleGuard(); }, []);

  // Global contact overlay — any component can fire this event
  useEffect(() => {
    const handler = (e) => {
      setContactSubject(e.detail?.subject || "");
      setContactOpen(true);
    };
    window.addEventListener("erpik:open-contact", handler);
    return () => window.removeEventListener("erpik:open-contact", handler);
  }, []);

  return (
    <>
      <ScrollToTop />
      {/* App-level safety net: a render throw in any route degrades to the
          ErrorBoundary fallback instead of white-screening the whole SPA.
          Keyed by pathname so navigating away from a crashed route clears
          the error — the nav, cursor and terminal live OUTSIDE this boundary
          and keep working regardless. */}
      <ErrorBoundary key={pathname}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<SamErpik />} />
            <Route path="/case/:slug" element={<CaseStudy />} />
            <Route path="/lab" element={<Lab />} />
            <Route path="/writing" element={<Writing />} />
            <Route path="/writing/:slug" element={<Post />} />
            <Route path="/garage" element={<Garage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <CustomCursor />
      <CommandPalette />
      {/* Ask Sam — accessible on every route via backtick or the floating pill */}
      <Terminal />
      <ThemeToast />
      <ContactOverlay
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        subject={contactSubject}
      />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppLoader>
        <DarkModeProvider>
          <div className="App grain" data-testid="app-root">
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </div>
        </DarkModeProvider>
      </AppLoader>
    </HelmetProvider>
  );
}

export default App;
